import React, {
  memo, useState, useMemo, useCallback, useEffect,
} from 'react'
import Image from 'next/legacy/image'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import Paper from '@mui/material/Paper'
import { Swiper, SwiperSlide } from 'swiper/react'
import NoVehicleImage from 'public/img/Nofotos_proximo.jpeg'
import { Navigation } from 'swiper'
import Typography from '@mui/material/Typography'
import NumberFormat from 'react-number-format'
import Grid from '@mui/material/Grid2'
import { useDispatch } from 'react-redux'
import {
  formatStateToShortName,
  getRandomPeopleRequestingCredit,
  getRandomPeopleWatchingCard,
} from 'utils/miscellaneous'
import {
  getVehicleStatus,
  getVehicleDiscounts,
  getVehicleTitle,
  formatLargeTransmission,
  getVehicleType,
  showCredit,
  getBranchName,
  getVehicleWarrantyConfig,
} from 'utils/vehicles'
import { setValue } from 'redux/modules/search'
import VehicleStatusAndLocation from 'components/VDP/VehicleStatusAndLocation'
import Box from '@mui/material/Box'
import { Button } from '@mui/material'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import usePageProps from 'hooks/usePageProps'
import SpinCarDialog from 'components/VDP/SpinCar'
import NoImage from 'public/img/No_foto_AutoSRP.webp'
import GreenWallet from 'components/icons/green-wallet.svg'
import BlueFlame from 'components/icons/blue_flame.svg'
import LocationIcon from 'components/icons/location.svg'
import { titleCaps } from 'utils/universal'
import BranchIcon from 'components/icons/branchIcon.svg'
import { EASY_TYPE, PARTICULAR_TYPE, VDP_TYPE } from 'enums'
import DicountBanner from 'components/VDP/DicountBanner'
import CardLink from 'components/SRP/CardLink'
import FlameAnimation from 'components/SRP/FlameAnimation'
import { applyThousandSeparator } from 'react-number-format/lib/utils'
import sx from './styles'

const VehicleCard = (props) => {
  const {
    vehicle,
    className: classNameProp = '',
    direction = 'column',
    swiper = false,
    href,
    sx: sxProp = {},
    priority = false,
    swiperProps = {},
    isB2BVehicle = false,
  } = props
  const {
    hasDiscount,
    category,
    endPromotionDate,
    price,
    precioTachado,
  } = useMemo(() => getVehicleDiscounts(vehicle), [vehicle])

  const isProximo = vehicle?.status === 'ONGOING'

  const { temporaryImageDomain, quality = 60 } = usePageProps()
  const dispatch = useDispatch()
  const [showThreeSixty, setShowThreeSixty] = useState(false)
  const [showCreditPersons, setShowCreditPersons] = useState(null)
  const [randomPeopleRequestingCredit, setRandomPeopleRequestCredit] = useState(null)
  const [randomPeopleWatchingCard, setRandomPeopleWatchingCard] = useState(null)
  const { formatMessage: f } = useIntl()
  const vehicleType = getVehicleType(vehicle)
  const isClikAutoVDP = vehicleType === VDP_TYPE.CLIKAUTO
  const isAgencyVDP = vehicleType === VDP_TYPE.AGENCIA || vehicleType === VDP_TYPE.PARTNER
  const showHitchFrom = (
    (price && (isClikAutoVDP || isAgencyVDP) && vehicle?.year > 2013)
    || (price
      && vehicleType === VDP_TYPE.PARTICULAR
      && (vehicle?.vehicleInspection?.acceptFinancing || vehicle?.acceptFinancing)
      && vehicle?.year > 2013)
  )
  const autoWarranty = (vehicle?.classWarranty && (isAgencyVDP || isClikAutoVDP))
  const showWarrantyTag = autoWarranty

  const variant = '3'
  const showTagBannerandCreditPersons = false
  const consignaVirtual = vehicle?.easy && (vehicle?.holdingType === 'virtual')
  const isAutocom = vehicle?.ownerType === EASY_TYPE.AGENCIA
        && vehicle?.holdingType === PARTICULAR_TYPE.VIRTUAL
  const consignaExist = !(vehicle?.easy && vehicle?.holdingType === null)
  const toggleShowThreeSixty = useCallback((e) => {
    e.preventDefault()
    setShowThreeSixty((prev) => !prev)
    return false
  }, [])

  const className = useMemo(() => clsx('VehicleCard-root', classNameProp, direction), [classNameProp, direction])
  const titleCar = useMemo(() => getVehicleTitle(vehicle), [vehicle])
  const vehicleStatus = useMemo(() => getVehicleStatus(vehicle), [vehicle])

  /**
   * Un auto vendido no lleva a ningún lado: su VDP redirige al listado. Se
   * desactiva el enlace y el handler de clic, que además dispara estado de
   * Redux pensado para la ficha de detalle.
   */
  const isSold = vehicleStatus === 'ARCHIVED'

  const showCreditText = showCredit(vehicle)
  const state = vehicleType === VDP_TYPE.PARTICULAR ? vehicle?.branch?.state : vehicle.state
  const f1 = useMemo(() => f({ id: 'PEOPLE_REQUESTING_CREDIT' },
    { number: randomPeopleRequestingCredit }), [randomPeopleRequestingCredit])
  const f2 = useMemo(() => f({ id: 'PEOPLE_WATCHING_CAR' },
    { number: randomPeopleWatchingCard }), [randomPeopleWatchingCard])

  const renderImage = useCallback((image, threeSixtyEnabled = false) => {
    return (
      <>
        {image ? (
          <Image
            onClick={threeSixtyEnabled ? toggleShowThreeSixty : null}
            src={`${temporaryImageDomain}/300x200/filters:format(webp)/filters:quality(${quality})/${image.filename}`}
            blurDataURL={`${temporaryImageDomain}/10x6/filters:blur(90)/filters:format(webp)/filters:quality(${quality})/${image.filename}`}
            placeholder="blur"
            layout="fill"
            objectFit="cover"
            priority={priority}
            title={`Autos seminuevos y autos usados ${titleCar} al mejor precio en ClikAuto`}
            alt={`Autos seminuevos, ${titleCar}`}
            unoptimized
          />
        ) : (
          <Image
            src={NoImage}
            layout="fill"
            objectFit="contain"
            alt="Default photo"
            title={`Autos seminuevos, ${titleCar}`}
          />
        )}

        {threeSixtyEnabled && (
          <Button
            id={`button-see-360-srp-${vehicle.id}`}
            sx={sx.button360}
            size="small"
            variant="contained"
            onClick={toggleShowThreeSixty}
            aria-label={f({ id: 'SEE_360' })}
          >
            <AutorenewIcon />
            360 &deg;
          </Button>
        )}
      </>
    )
  }, [
    titleCar,
    vehicle,
  ])

  const holdFrame = useMemo(() => (
    <Box sx={sx.hold}>
      {
        isProximo ? (
          <Image
            src={NoVehicleImage}
            layout="fill"
            style={{ objectFit: 'cover' }}
            placeholder="blur"
            alt="Próximo Auto"
            title="Próximo Auto"
            unoptimized
          />
        ) : (
          <Typography variant="body1">
            {titleCaps(f({ id: 'VEHICLE_STATUS_HOLD' }))}
          </Typography>
        )
      }
    </Box>
  ), [])

  const profilePhoto = useMemo(() => (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        height: 130,
      }}
    >
      {renderImage(vehicle?.images?.[0])}
      {(vehicleStatus === 'HOLD' || vehicleStatus === 'HOLD_ONLINE') && holdFrame}
    </Box>
  ), [vehicle])

  const getFirstNPhotos = useCallback((n) => ([
    vehicle?.images[0],
    ...vehicle?.images
      .filter((_, i) => i > 0)
      .sort(() => Math.random() - 0.5)
      .filter((_, i) => i < n - 1),
  ]), [vehicle])

  const imageSwiper = useMemo(() => (
    <Box
      sx={sx.imageSwiper}
      className={clsx({
        mt: !(showCreditText),
      })}
    >
      {(vehicleStatus === 'HOLD' || vehicleStatus === 'HOLD_ONLINE' || isProximo) && holdFrame}

      <Swiper
        navigation={vehicleStatus !== 'HOLD'}
        modules={[Navigation]}
        slidesPerView={2}
        spaceBetween={2}
        preventClicksPropagation
        {...swiperProps}
      >
        {vehicle?.images?.length ? (
          getFirstNPhotos((vehicleStatus === 'HOLD' || vehicleStatus === 'HOLD_ONLINE' || isProximo) ? 2 : 4)
            .map((image) => {
              // const show360Button = i === 7 && vehicle.spinCarUrl
              return (
                <SwiperSlide key={image.filename}>
                  {renderImage(image, false)}
                </SwiperSlide>
              )
            })
        ) : (
          <>
            <SwiperSlide>{renderImage()}</SwiperSlide>
            <SwiperSlide>{renderImage()}</SwiperSlide>
          </>
        )}
      </Swiper>
    </Box>
  ), [vehicle, showCreditText])

  const handleOnClickItem = useCallback(() => {
    dispatch(setValue({
      key: 'userViews',
      value: showCreditText && showCreditPersons
        ? randomPeopleRequestingCredit
        : randomPeopleWatchingCard,
      persist: true,
    }))
    dispatch(setValue({
      key: 'showCreditPersons',
      value: showCreditText && showCreditPersons,
      persist: true,
    }))
  }, [showCreditPersons, randomPeopleRequestingCredit, randomPeopleWatchingCard, showCreditText])

  const tagsBanner = (
    <Box
      sx={sx.tagsBanner}
      className={clsx({
        [`${getVehicleStatus(vehicle)}`
          .toLowerCase()]: `${getVehicleStatus(vehicle)}`.toLowerCase(),
        easy: vehicle?.consignacion,
      })}
    >
      <Typography
        variant="quote"
        sx={sx.suscriptionText}
      >

        {vehicle?.subscriptionActive && !vehicle?.consignacion && (
          <Typography
            variant="quote"
            component="span"
          >
            <FormattedMessage id="SUSCRIPTION_TEXT" />
            {' '}
            &bull;
          </Typography>
        )}

        {showCreditText && (
          <Typography
            variant="quote"
            component="span"
          >
            <FormattedMessage id={vehicleType === 'clikauto'
              ? 'CREDIT_TEXT' : 'CREDIT_TEXT_2'}
            />
            {!vehicle?.consignacion && (
              <>
                {' '}
                &bull;
              </>
            )}
          </Typography>
        )}

        {!vehicle?.consignacion && (
          <Typography
            variant="quote"
            component="span"
          >
            <FormattedMessage id="WE_GET_YOUR_CAR_TEXT" />
          </Typography>
        )}

      </Typography>
    </Box>
  )

  const hitchFrom = ({ hitchAmount }) => (
    <Box>
      <Typography
        variant="caption"
      >
        <Typography>
          <FormattedMessage
            id="HITCH_FROM"
            values={{
              monthlyInstallment: applyThousandSeparator(`$${Math.ceil(hitchAmount / 1000) * 1000}`, ','),
              span: (chunks) => <span>{chunks}</span>,
            }}
          />
        </Typography>
      </Typography>
    </Box>
  )

  useEffect(() => {
    setShowCreditPersons(Math.floor(Math.random() * 2) === 0)
    setRandomPeopleRequestCredit(getRandomPeopleRequestingCredit())
    setRandomPeopleWatchingCard(getRandomPeopleWatchingCard())
  }, [])

  const { showOneYearWarranty, showThreeMonthWarranty } = getVehicleWarrantyConfig(vehicle)

  return (
    <CardLink
      disabled={isSold}
      href={href}
    >
      <Paper
        sx={{ ...sx.root, ...sxProp, ...(isSold ? { cursor: 'default' } : {}) }}
        className={clsx(className, {
          hasDiscount,
          withoutHitch: !showHitchFrom,
        })}
        elevation={0}
        onClick={isSold ? undefined : handleOnClickItem}
        title={`Autos seminuevos, ${titleCar}`}
      >
        {(showCreditText && showTagBannerandCreditPersons) && (
          tagsBanner
        )}
        <Grid
          container
          spacing={1}
        >
          <Grid size={direction === 'row' ? 6 : 12}>
            {hasDiscount && (
              /*
                `top` se calcula contra el alto del área de imagen (130px, ver
                `imageSwiper` en los estilos de esta tarjeta): la flama hace que
                el bloque mida 44px de alto, así que 78 + 44 = 122 lo deja
                dentro de la foto con 8px de respiro y sin montarse sobre la
                zona blanca de la tarjeta.
              */
              <DicountBanner
                text="VEHICLE_STATUS_WITH_DISCOUNT"
                top="78px"
                zIndex="99"
                left="10px"
                bgcolor="#DD1E33"
                adornment={<FlameAnimation size={44} />}
              />
            )}
            {(swiper && vehicle?.images?.length > 0) ? imageSwiper : profilePhoto}
          </Grid>
          <Grid
            size="grow"
            mx={2}
          >
            <VehicleStatusAndLocation
              status={vehicleStatus}
              hasDiscount={hasDiscount}
              category={category}
              state={vehicle.state}
              sellerName={vehicle.sellerName}
              showLocation={false}
              endPromotionDate={endPromotionDate}
              sx={sx.tiles}
              variant={variant}
              vehicleType={vehicleType}
              isB2BVehicle={isB2BVehicle}
              isProximo={isProximo}
              showWarrantyTag={showWarrantyTag}
              showOneYearWarranty={showOneYearWarranty}
              showThreeMonthWarranty={showThreeMonthWarranty}
            />
          </Grid>

          {showTagBannerandCreditPersons && (
            <Grid
              size={12}
              display="flex"
              mx={2}
              sx={sx.showCreditContainer}
            >
              {(showCreditPersons !== null) && (
                <>
                  {showCreditText && showCreditPersons ? <GreenWallet /> : <BlueFlame />}
                  <Typography
                    variant="strong"
                    sx={sx.peopleAction(showCreditText && showCreditPersons
                      ? 'success.dark' : 'text.blue')}
                  >
                    {showCreditText && showCreditPersons ? f1 : f2}
                  </Typography>
                </>
              )}
            </Grid>
          )}

          <Grid
            size={direction === 'row' ? 6 : 12}
            sx={sx.vehicleInfoContainer}
            className={clsx({
              minHeight: showCreditPersons !== null && hasDiscount,
            })}
          >
            <Typography
              variant="h3"
              gutterBottom
              sx={sx.titleCar}
            >
              {titleCar}
            </Typography>

            <Typography
              sx={sx.breakSpace}
              variant="body2"
              noWrap
            >
              {vehicle.trim && (
                <>
                  <span>{titleCaps(vehicle.trim)}</span>
                  &bull;
                </>
              )}

              <span>
                <NumberFormat
                  value={vehicle.mileage}
                  suffix=" kms"
                  thousandSeparator
                  displayType="text"
                />
                &bull;
              </span>

              {vehicle.cesviTransmission && (
                <>
                  <span>
                    {formatLargeTransmission(vehicle?.cesviTransmission)}
                  </span>
                  &bull;
                </>
              )}

              {vehicle.cesviFuel && (
                <span>{titleCaps(vehicle.cesviFuel)}</span>
              )}
            </Typography>

            <Box sx={sx.priceContainer}>
              <Typography
                variant="h3"
                component="h3"
                sx={sx.price((hasDiscount) ? 'common.purple' : 'text.magenta')}
              >
                <NumberFormat
                  value={price}
                  displayType="text"
                  prefix="$"
                  thousandSeparator
                  decimalScale={0}
                />
              </Typography>
              {(hasDiscount && precioTachado > 0) && (
                <Typography
                  variant="strong"
                  component="p"
                  sx={{ textDecoration: 'line-through' }}
                >
                  <NumberFormat
                    value={precioTachado}
                    displayType="text"
                    prefix="$"
                    thousandSeparator
                    decimalScale={0}
                  />
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid
            size={12}
            sx={sx.vehicleInfoContainer}
          >
            {showHitchFrom && (
              hitchFrom({ hitchAmount: price * 0.20 })
            )}
          </Grid>

          <Grid
            container
            sx={sx.sellerContainer}
            mt={1}
          >
            {
              consignaExist && (
                <Grid
                  size="auto"
                  pr={1}
                >
                  <Typography
                    variant="body1"
                    sx={sx.sellerType}
                  >
                    <BranchIcon />
                    {isProximo
                      ? f({ id: 'SRP_CARD_CLIKAUTO_LOCATION' })
                      : vehicle?.easy
                        ? (consignaVirtual
                          ? f({ id: isAutocom ? 'VDP_CLIKAUTO_EASY_CONSIGNACION_AUTOCOM' : 'VDP_CLIKAUTO_EASY_CONSIGNACION_VIRTUAL' })
                          : f({ id: 'VDP_CLIKAUTO_EASY_CONSIGNACION_FISICA' }))
                        : getBranchName(vehicle?.seller?.branch?.name)}
                  </Typography>
                </Grid>
              )
            }
            {state && (
              <Grid
                size="auto"
                pr={1}
              >
                <Typography
                  variant="body1"
                  sx={sx.locationText}
                >
                  <LocationIcon />

                  {
                    (isProximo) ? (
                      <>
                        {f({ id: 'VDP_CLIKAUTO_EASY_CONSIGNACION_NULL' })}
                      </>
                    ) : (
                      <>
                        {formatStateToShortName(state)}
                      </>
                    )
                  }

                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        <SpinCarDialog
          title={titleCar}
          open={showThreeSixty}
          onClose={toggleShowThreeSixty}
          url={vehicle.spinCarUrl}
        />
      </Paper>
    </CardLink>
  )
}

export default memo(VehicleCard)
